import styled, { css } from 'styled-components';
import arrowUp from '~/assets/icons/arrow_up.svg';
import itemBg from '~/assets/images/shop/clothesShop_item_bg.png';
import newShadow from '~/assets/images/shop/new_shadow.png';
import { Text } from '../Text';

const StyledImageBg = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	mask-image: url(${itemBg});
	mask-size: 100% 100%;
	mask-repeat: no-repeat;
	mask-position: center;
	-webkit-mask-image: url(${itemBg});
	-webkit-mask-size: 100% 100%;
	-webkit-mask-repeat: no-repeat;
	-webkit-mask-position: center;
	background-color: #ffffff;
	transition: background-color 0.3s ease;
`;

const StyledInfoContainer = styled.div`
	padding: 0 16px;
	display: flex;
	justify-content: space-between;
`;

const StyledCategory = styled(Text)`
	margin-top: 14px;
	font-size: 12px;
	color: #ffffff;
	opacity: 0.25;
	transition: color 0.3s ease;
`;

const StyledTitle = styled.div`
	margin-top: 4px;
	display: flex;
	align-items: center;
	gap: 4px;

	${Text} {
		color: #ffffff;
		transition: color 0.3s ease;

		&:first-child {
			font-weight: 500;
			font-size: 14px;
			max-width: 161px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		&:nth-child(2) {
			font-weight: 400;
			font-size: 14px;
			opacity: 0.5;
		}
	}
`;

const StyledPriceContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 11px;

	img {
		transition: filter 0.3s ease;
	}

	${Text} {
		font-weight: 700;
		font-size: 14px;
		color: #ffffff;
		transition: color 0.3s ease;
	}
`;

const StyledPricesContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const StyledOldPriceContainer = styled(StyledPriceContainer)`
	position: relative;

	img,
	${Text} {
		opacity: 0.25;
	}

	.line {
		position: absolute;
		top: 50%;
		left: -3px;
		transform: translateY(-50%);
		width: calc(100% + 6px);
		height: 1px;
		background-color: #ffffff;
		opacity: 0.5;
		transition: background-color 0.3s ease;
	}
`;

const StyledVariationButtons = styled.div`
	opacity: 0;
	transition: opacity 0.3s ease;
	pointer-events: none;
	display: flex;
	flex-direction: column;
	gap: 0;
	height: 60px;
	margin-top: 15px;
`;

const StyledArrow = styled.div`
	width: 12px;
	height: 12px;
	mask-image: url(${arrowUp});
	mask-size: 100% 100%;
	mask-repeat: no-repeat;
	mask-position: center;
	-webkit-mask-image: url(${arrowUp});
	-webkit-mask-size: 100% 100%;
	-webkit-mask-repeat: no-repeat;
	-webkit-mask-position: center;
	background-color: #ffffff;
	transition: background-color 0.3s ease;
`;

const StyledVariationButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	background-color: rgba(255, 255, 255, 0.1);
	transition:
		background-color 0.3s ease,
		border-bottom 0.3s ease,
		border-top 0.3s ease;
	cursor: pointer;

	&:hover {
		background-color: #ffffff;

		${StyledArrow} {
			background-color: #000000;
		}
	}
`;

const StyledUpButton = styled(StyledVariationButton)`
	border-radius: 10px 10px 0 0;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const StyledDownButton = styled(StyledVariationButton)`
	border-radius: 0 0 10px 10px;
	border-top: 1px solid rgba(0, 0, 0, 0.25);

	${StyledArrow} {
		transform: rotate(180deg);
	}
`;

const StyledImageContainer = styled.div`
	position: relative;
	width: 90px;
	height: 90px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-right: 1px solid rgba(255, 255, 255, 0.1);
	transition: border-right 0.3s ease;
`;

const StyledContainer = styled.div<{
	$invertCurrency: boolean;
	$active: boolean;
}>`
	display: grid;
	grid-template-columns: 90px 1fr;
	width: 100%;
	height: 90px;
	background-color: rgba(255, 255, 255, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	transition: background-color 0.3s ease;
	cursor: pointer;

	${(props) =>
		props.$active &&
		css`
			background-color: #2d70da;

			${StyledVariationButtons} {
				opacity: 1;
				pointer-events: auto;
			}
		`}

	${({ $invertCurrency, $active }) =>
		!$active &&
		css`
			&:hover {
				background-color: #ffffff;

				${StyledImageBg} {
					background-color: #000000;
				}

				${StyledCategory}, ${StyledTitle} ${Text}, ${StyledPriceContainer} ${Text} {
					color: #000000;
				}

				${StyledImageContainer} {
					border-right: 1px solid rgba(0, 0, 0, 0.1);
				}

				${$invertCurrency
					? css`
							${StyledPriceContainer} img, ${StyledOldPriceContainer} img {
								filter: invert(1);
							}
					  `
					: css`
							${StyledPriceContainer} img, ${StyledOldPriceContainer} img {
								filter: brightness(0);
							}
					  `}

				${StyledOldPriceContainer} {
					.line {
						background-color: #000000;
					}
				}

				${StyledUpButton} {
					border-bottom: 1px solid transparent;
				}

				${StyledDownButton} {
					border-top: 1px solid transparent;
				}
			}
		`}
`;

const StyledNewDot = styled.div`
	position: absolute;
	top: 15px;
	left: 15px;
	z-index: 1;
	width: 8px;
	height: 8px;
	border-radius: 8px;
	background: #eb1818;
`;

const StyledNewShadow = styled.div`
	position: absolute;
	top: 8px;
	left: 8px;
	z-index: 0;
	width: 22px;
	height: 22px;
	background-image: url(${newShadow});
	background-repeat: no-repeat;
	background-position: center;
	background-size: contain;
`;

const StyledImageWrapper = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 60px;
	height: 60px;
`;

export {
	StyledArrow,
	StyledCategory,
	StyledContainer,
	StyledDownButton,
	StyledImageBg,
	StyledImageContainer,
	StyledImageWrapper,
	StyledInfoContainer,
	StyledNewDot,
	StyledNewShadow,
	StyledOldPriceContainer,
	StyledPriceContainer,
	StyledPricesContainer,
	StyledTitle,
	StyledUpButton,
	StyledVariationButtons,
};
